import ConfirmDialog from "@components/confirm-dialog";
import {DeleteIcon, EditIcon} from "@components/icons";
import {format} from "@constants";
import {api} from "@core/api";
import {ChatSessionSummary} from "@core/model";
import {Selectors} from "@core/store/selectors";
import {
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {ArrowBack, Check, Close} from "@mui/icons-material";
import {translate} from "@util";
import dayjs from "dayjs";
import {useState} from "react";
import {useSelector} from "react-redux";

type Props = {
  activeSessionId?: string;
  onBack: () => void;
  onSelect: (id: string) => void;
  onDeleted: (id: string) => void;
};

const sessionTime = (value: string) =>
  dayjs(value).format(format.datetime.short);

const ChatSessions = ({
  activeSessionId,
  onBack,
  onSelect,
  onDeleted,
}: Props) => {
  const tenantId = useSelector(Selectors.tenantId);
  const {currentData: sessions, isFetching} = api.useFetchChatSessionsQuery(
    tenantId ?? "",
    {skip: !tenantId, refetchOnMountOrArgChange: true},
  );
  const [deleteSession, {isLoading: deleting}] =
    api.useDeleteChatSessionMutation();
  const [pendingDelete, setPendingDelete] = useState<ChatSessionSummary>();
  const [renameSession, {isLoading: renaming}] =
    api.useRenameChatSessionMutation();
  const [editingId, setEditingId] = useState<string>();
  const [draftTitle, setDraftTitle] = useState("");

  const startRename = (session: ChatSessionSummary) => {
    setEditingId(session.id);
    setDraftTitle(session.title);
  };

  const cancelRename = () => {
    setEditingId(undefined);
    setDraftTitle("");
  };

  const confirmRename = async (session: ChatSessionSummary) => {
    const title = draftTitle.trim();
    if (!title || title === session.title) {
      cancelRename();
      return;
    }
    try {
      await renameSession({id: session.id, body: {title}}).unwrap();
      cancelRename();
    } catch {
      // resta in modifica: l'utente puo riprovare o annullare
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    const id = pendingDelete.id;
    try {
      await deleteSession(id).unwrap();
      onDeleted(id);
    } catch {
      // l'errore resta sul pannello principale se serve
    } finally {
      setPendingDelete(undefined);
    }
  };

  return (
    <Stack flex={1} minHeight={0}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        px={2}
        py={1.5}
        borderBottom={1}
        borderColor="divider">
        <IconButton
          aria-label={translate("chatbot.history.back")}
          onClick={onBack}
          size="small">
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography variant="h6" component="h2" flex={1} noWrap>
          {translate("chatbot.history.title")}
        </Typography>
      </Stack>

      {isFetching && !sessions?.length ? (
        <Stack flex={1} alignItems="center" justifyContent="center">
          <CircularProgress size={32} />
        </Stack>
      ) : !sessions?.length ? (
        <Stack flex={1} px={2} py={3} justifyContent="center">
          <Typography color="text.secondary" textAlign="center">
            {translate("chatbot.history.empty")}
          </Typography>
        </Stack>
      ) : (
        <List
          dense
          disablePadding
          sx={{flex: 1, minHeight: 0, overflow: "auto", py: 0.5}}>
          {sessions.map(session =>
            session.id === editingId ? (
              <Stack
                key={session.id}
                direction="row"
                alignItems="center"
                gap={0.5}
                px={2}
                py={1}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  value={draftTitle}
                  disabled={renaming}
                  inputProps={{
                    "aria-label": translate("chatbot.history.renameField"),
                    maxLength: 120,
                  }}
                  onChange={event => setDraftTitle(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      confirmRename(session);
                    } else if (event.key === "Escape") {
                      event.preventDefault();
                      cancelRename();
                    }
                  }}
                />
                <IconButton
                  aria-label={translate("chatbot.history.renameSave")}
                  size="small"
                  disabled={renaming || !draftTitle.trim()}
                  onClick={() => confirmRename(session)}>
                  <Check fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label={translate("chatbot.history.renameCancel")}
                  size="small"
                  disabled={renaming}
                  onClick={cancelRename}>
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <ListItemButton
                key={session.id}
                selected={session.id === activeSessionId}
                onClick={() => onSelect(session.id)}
                sx={{alignItems: "flex-start", gap: 0.5, pr: 1}}>
                <ListItemText
                  primary={session.title}
                  secondary={sessionTime(session.updatedAt)}
                  primaryTypographyProps={{noWrap: true}}
                />
                <IconButton
                  aria-label={translate("chatbot.history.rename")}
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    startRename(session);
                  }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label={translate("chatbot.history.delete")}
                  edge="end"
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    setPendingDelete(session);
                  }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ),
          )}
        </List>
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        loading={deleting}
        label={{
          title: translate("chatbot.history.deleteConfirm.title"),
          text: pendingDelete?.title,
          confirm: translate("chatbot.history.deleteConfirm.confirm"),
          cancel: translate("chatbot.history.deleteConfirm.cancel"),
        }}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(undefined)}
      />
    </Stack>
  );
};

export {ChatSessions};
